import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Hash, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

export function BlogList() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/blog/articles?limit=50`);
        setArticles(res.data.articles || []);
      } catch (e) {
        // silencieux
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-void">
      <header className="border-b border-gray-800 py-4 px-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-orbitron text-xl font-bold text-cyan-400">L'EFFACEUR</Link>
          <Link to="/" className="text-gray-400 hover:text-cyan-400 flex items-center gap-2 text-sm font-mono">
            <ArrowLeft className="w-4 h-4" /> Retour
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-12">
        <h1 className="font-orbitron text-3xl md:text-4xl font-bold text-white mb-2">
          BLOG <span className="text-cyan-400">L'EFFACEUR</span>
        </h1>
        <p className="text-gray-400 font-mono text-sm mb-12">// GUIDES_RÉSILIATION · DROITS_CONSOMMATEURS</p>

        {loading ? (
          <div className="text-center py-12 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin inline mr-2" />
            Chargement…
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-12 text-gray-500 font-mono">
            Aucun article publié pour le moment.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {articles.map(a => (
              <Link key={a.article_id} to={`/blog/${a.slug}`} className="glass-card border border-gray-800 p-6 hover:border-cyan-500/50 transition-all block">
                <h2 className="font-orbitron text-lg font-bold text-white mb-2">{a.title}</h2>
                <p className="text-gray-400 text-sm mb-4">{a.meta_description}</p>
                <div className="flex items-center gap-3 text-xs text-gray-500 font-mono">
                  {a.published_at && <span><Calendar className="w-3 h-3 inline mr-1" />{new Date(a.published_at).toLocaleDateString('fr-FR')}</span>}
                  {a.word_count && <span>{a.word_count} mots</span>}
                </div>
                {a.tags && a.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {a.tags.slice(0, 4).map((t, i) => (
                      <span key={i} className="px-2 py-0.5 rounded bg-gray-800 text-cyan-400 text-xs font-mono">
                        <Hash className="w-3 h-3 inline" />{t}
                      </span>
                    ))}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/blog/articles/${slug}`);
        setArticle(res.data.article);
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-void flex items-center justify-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mr-2" />Chargement…</div>;
  if (error || !article) return (
    <div className="min-h-screen bg-void flex items-center justify-center">
      <div className="text-center">
        <p className="text-gray-400 mb-4">Article introuvable</p>
        <Link to="/blog" className="text-cyan-400 hover:underline">← Retour au blog</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-void">
      <header className="border-b border-gray-800 py-4 px-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-orbitron text-xl font-bold text-cyan-400">L'EFFACEUR</Link>
          <Link to="/blog" className="text-gray-400 hover:text-cyan-400 flex items-center gap-2 text-sm font-mono">
            <ArrowLeft className="w-4 h-4" /> Blog
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 py-12 text-gray-200">
        <h1 className="font-orbitron text-3xl md:text-4xl font-bold text-white mb-4">{article.title}</h1>
        <p className="text-gray-400 text-sm mb-8 font-mono">
          {article.published_at && new Date(article.published_at).toLocaleDateString('fr-FR')} · {article.word_count} mots
        </p>

        {(article.sections || []).map((s, i) => (
          <section key={i} className="mb-8">
            {s.h2 && <h2 className="font-orbitron text-xl font-bold text-cyan-400 mb-3">{s.h2}</h2>}
            <div className="leading-relaxed whitespace-pre-wrap text-gray-300">{s.content}</div>
          </section>
        ))}

        {article.cta && (
          <div className="glass-card border border-cyan-500/40 p-6 my-8 text-center">
            <p className="text-white mb-4">{article.cta}</p>
            <Link to="/register" className="inline-block bg-cyan-500 text-black font-bold px-6 py-3 rounded hover:bg-cyan-400">
              Découvrir L'EFFACEUR
            </Link>
          </div>
        )}

        {article.tags && article.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8 pt-8 border-t border-gray-800">
            {article.tags.map((t, i) => (
              <span key={i} className="px-2 py-1 rounded bg-gray-800 text-cyan-400 text-xs font-mono">#{t}</span>
            ))}
          </div>
        )}
      </article>
    </div>
  );
}

export default BlogList;
